import React from 'react'
import styled, { css } from 'styled-components'

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import DeleteIcon from '@mui/icons-material/Delete'

import KeyboardAccessible from 'components/KeyboardAccessible'
import Tooltip from 'components/Tooltip'

import { shortDate } from 'helpers/formatters'

import { color } from 'styles/colors'

type MutedNotificationItemProps = {
  name: string
  createdAt: string
  markedForDeletion: boolean
  children: React.ReactNode
  onToggleMuteCallback: () => void
}

const MutedNotificationItem = ({
  name,
  createdAt,
  markedForDeletion,
  children,
  onToggleMuteCallback
}: MutedNotificationItemProps) => {
  return (
    <S.Line markedForDeletion={markedForDeletion}>
      <S.NotificationDescription>
        {children}
      </S.NotificationDescription>

      <S.NotificationInfo>
        <b>muted on {shortDate(createdAt)}</b>
        {markedForDeletion
          ? (
            <>
              <Tooltip title="Add Back">
                <S.DeleteIconContainer
                  onClick={() => onToggleMuteCallback()}
                >
                  <S.DeleteIcon
                    role="img"
                    aria-label={`Add Back Mute for ${name}`}
                  />
                </S.DeleteIconContainer>
              </Tooltip>
              <i>will be deleted when the form is saved</i>
            </>
          ) : (
            <Tooltip title="Remove">
              <S.DeleteIconContainer
                onClick={() => onToggleMuteCallback()}
              >
                <S.DeleteOutlinedIcon
                  role="img"
                  aria-label={`Remove Mute for ${name}`}
                />
              </S.DeleteIconContainer>
            </Tooltip>
          )
        }
      </S.NotificationInfo>
    </S.Line>
  )
}

export default MutedNotificationItem

const iconStyle = (iconType: string) => {
  return css`
    margin: 0 0 -5px;
    cursor: pointer;
    color: ${color(iconType)};

    &:focus {
      outline: none;
    }

    &:hover {
      color: ${color(`${iconType}Hover`)};
    }
  `
}

const S = {
  Line: styled.div.withConfig({
    shouldForwardProp: (prop) => !['markedForDeletion'].includes(prop)
  })< { markedForDeletion?: boolean } >`
    ${({ markedForDeletion }) => markedForDeletion && css`
      span, b {
        color: ${color('disabledText')};

        &:hover {
          color: ${color('disabledText')};
        }
      }
    `}
  `,
  NotificationDescription: styled.div`
    display: inline-flex;
    margin: 0 -5px 0 0;
  `,
  NotificationInfo: styled.div`
    display: inline-block;
    margin-left: 10px;
  `,
  DeleteIconContainer: styled(KeyboardAccessible)`
    margin-left: 5px;
  `,
  DeleteIcon: styled(DeleteIcon)`
    ${iconStyle('destructiveIcon')}
  `,
  DeleteOutlinedIcon: styled(DeleteOutlinedIcon)`
    ${iconStyle('destructiveIcon')}
  `
}
